import { DB_FIELD_MAX_LENGTH } from "@selector/shared/settings";
import { Component } from "react";
import { connect } from "react-redux";
import { Form, formValueSelector, getFormValues, type InjectedFormProps, reduxForm, SubmissionError } from "redux-form";

import { getFormError, getFormWarning, handleTabInForm } from "../../../app-utils/form.js";
import { type Group, type Organism, type Project } from "../../../graphql.js";
import { getProjectLabel } from "../../../redux/modules/config.js";
import {
  type IPlaybookExecutionState,
  updatePlaybookExecutionState,
} from "../../../redux/modules/playbook-execution.js";
import { type IAppState } from "../../../redux/reducer.js";
import FormSection from "../../core/form/layout/FormSection.js";
import { getSelectedOrganismFromForm } from "../../core/forms/AttributeInputField.js";
import ProjectsSelect from "../../core/forms/ProjectsSelect.js";
import TabFence from "../../core/forms/TabFence.js";
import InputField, { InputReduxFormField } from "../../core/redux-form/InputField.js";
import { FieldTitle } from "../FieldTitle.js";
import PlaybookWizardStep, {
  type IPlaybookWizardStepInjectedProps,
  type IPlaybookWizardStepProvidedProps,
} from "../PlaybookWizardStep.js";
import { ClassicExperimentLevelInputFields } from "./CombinedRegistrationNgsAnalysisDataScreen.js";

interface ICombinedRegistrationNgsRangeScreenOwnProps extends IPlaybookWizardStepProvidedProps {
  onSubmit?: () => Promise<Group | void>;
}

interface ICombinedRegistrationNgsRangeScreenAppStateProps {
  playbookExecution: IPlaybookExecutionState;
  globalFormError: string;
  globalFormWarning: string;
  selectedProject: Project;
  formValues: any;
  initialValues: Partial<FormData>;
}

interface ICombinedRegistrationNgsRangeScreenDispatchProps {
  updatePlaybookExecutionState: (state: Partial<IPlaybookExecutionState>) => void;
}

export interface ICombinedRegistrationNgsRangeScreenProps
  extends ICombinedRegistrationNgsRangeScreenAppStateProps,
    ICombinedRegistrationNgsRangeScreenDispatchProps,
    ICombinedRegistrationNgsRangeScreenOwnProps,
    InjectedFormProps<FormData, any>,
    IPlaybookWizardStepInjectedProps {
  // only if this screen is a redux-form
}

function mapStateToProps(
  state: IAppState,
  _ownProps: ICombinedRegistrationNgsRangeScreenProps,
): ICombinedRegistrationNgsRangeScreenAppStateProps {
  return {
    playbookExecution: state.playbookExecution,
    globalFormError: getFormError(CombinedRegistrationNgsRangeScreenImpl.COMBINED_REGISTRATION_NGS_RANGE_SCREEN, state),
    globalFormWarning: getFormWarning(
      CombinedRegistrationNgsRangeScreenImpl.COMBINED_REGISTRATION_NGS_RANGE_SCREEN,
      state,
    ),
    selectedProject: formValueSelector(CombinedRegistrationNgsRangeScreenImpl.COMBINED_REGISTRATION_NGS_RANGE_SCREEN)(
      state,
      CombinedRegistrationNgsRangeScreenImpl.FIELD_PROJECT,
    ),
    formValues: getFormValues(CombinedRegistrationNgsRangeScreenImpl.COMBINED_REGISTRATION_NGS_RANGE_SCREEN)(state),
    initialValues: { ...state.playbookExecution.workflowValues },
  };
}

function mapDispatchToProps(dispatch: any): ICombinedRegistrationNgsRangeScreenDispatchProps {
  return {
    updatePlaybookExecutionState: (state: Partial<IPlaybookExecutionState>) =>
      dispatch(updatePlaybookExecutionState(state)),
  };
}

/**
 * Screen used in range analyses to enter the range details (prefilled when called from Genome Viewer
 */
class CombinedRegistrationNgsRangeScreenImpl extends Component<ICombinedRegistrationNgsRangeScreenProps> {
  public static COMBINED_REGISTRATION_NGS_RANGE_SCREEN = "COMBINED_REGISTRATION_NGS_RANGE_SCREEN";

  public static FIELD_PROJECT = "project";
  public static FIELD_EVENT_NAME = "event_name";
  public static FIELD_EVENT_DESCRIPTION = "event_description";

  private lastTabRef: any;
  private firstTabRef: any;

  constructor(props: ICombinedRegistrationNgsRangeScreenProps) {
    super(props);
  }

  handleSubmitData = (values: Record<string, any>) => {
    // at this stage the validation has succeeded
    const selectedProject: Project = values[CombinedRegistrationNgsRangeScreenImpl.FIELD_PROJECT];
    const eventName: string = values[CombinedRegistrationNgsRangeScreenImpl.FIELD_EVENT_NAME];
    const eventDescription: string = values[CombinedRegistrationNgsRangeScreenImpl.FIELD_EVENT_DESCRIPTION];

    this.props.updatePlaybookExecutionState({
      project: selectedProject,
      eventName,
      eventDescription,
      workflowValues: { ...this.props.playbookExecution.workflowValues, ...values },
    });

    // we always submit directly from this screen
    return this.props.onSubmit().then(
      (_fulfilled) => this.props.proceedWithNextStep(),
      (rejected: Error) => {
        throw new SubmissionError({ _error: rejected.message });
      },
    );
  };

  pageDescription = () => {
    return `Select a destination for the data that are generated for this analysis.`;
  };

  render() {
    const selectedGenome: Organism = getSelectedOrganismFromForm(
      this.props.playbookExecution.workflowAttributes,
      this.props.formValues,
    );

    return (
      <PlaybookWizardStep
        {...this.props}
        playbookPage={{
          ...this.props.playbookPage,
          label: this.props.playbookPage.label || "Range Settings",
          description: this.pageDescription(),
        }}
        wizardFooterData={{
          error: this.props.globalFormError || this.props.error,
          warning: this.props.globalFormWarning,
          nextButtonProps: {
            label: "Submit",
            isDisabled: this.props.submitting,
            isLoading: this.props.submitting,
            onPress: this.props.handleSubmit(this.handleSubmitData),
          },
        }}
      >
        <Form
          className="form-horizontal"
          onSubmit={this.props.handleSubmit(this.handleSubmitData)}
          onKeyDownCapture={handleTabInForm}
        >
          <TabFence targetRef={this.lastTabRef} />
          <FormSection>
            <ProjectsSelect
              name={CombinedRegistrationNgsRangeScreen.FIELD_PROJECT}
              horizontalLayout
              title={<FieldTitle label={getProjectLabel(true, false)} mandatory />}
              tooltip={`Select the ${getProjectLabel(
                false,
                false,
              )} to which the analysis and its output will be attached`}
              onError={(error: Error) => this.props.updateWizardMessage(error.message)}
            />
            <InputReduxFormField
              horizontalLayout
              key={"event-name"}
              name={CombinedRegistrationNgsRangeScreen.FIELD_EVENT_NAME}
              label={<FieldTitle label={"Event name"} />}
              component={InputField}
              placeholder="Enter text..."
              maxLength={DB_FIELD_MAX_LENGTH.EVENT.NAME}
              tooltip={"You can provide a name for the playbook event"}
            />
            <InputReduxFormField
              horizontalLayout
              key={"event-description"}
              name={CombinedRegistrationNgsRangeScreen.FIELD_EVENT_DESCRIPTION}
              label={<FieldTitle label={"Event description"} />}
              component={InputField}
              placeholder="Enter text..."
              maxLength={DB_FIELD_MAX_LENGTH.EVENT.DESCRIPTION}
              tooltip={"You can provide a description for the playbook event"}
            />
          </FormSection>

          {this.props.initialized && ( // make sure the form is properly initialized before starting to mount the inputs, because some init logic might reset the init values if not compatible with some non ready data
            <ClassicExperimentLevelInputFields
              selectedWorkflow={this.props.playbookExecution.workflow}
              workflowAttributes={this.props.playbookExecution.workflowAttributes}
              playbook={this.props.playbookExecution.playbook}
              selectedGenome={selectedGenome}
              onlyExplorerGenome
            />
          )}

          <TabFence targetRef={this.firstTabRef} />
        </Form>
      </PlaybookWizardStep>
    );
  }
}

const CombinedRegistrationNgsRangeScreen = connect<
  ICombinedRegistrationNgsRangeScreenAppStateProps,
  ICombinedRegistrationNgsRangeScreenDispatchProps,
  ICombinedRegistrationNgsRangeScreenOwnProps
>(
  mapStateToProps,
  mapDispatchToProps,
)(
  reduxForm<any, ICombinedRegistrationNgsRangeScreenOwnProps>({
    form: CombinedRegistrationNgsRangeScreenImpl.COMBINED_REGISTRATION_NGS_RANGE_SCREEN,
  })(CombinedRegistrationNgsRangeScreenImpl as any) as any,
);
export { CombinedRegistrationNgsRangeScreen };
